import React from 'react';
import { ImageSuspensionNotice } from '@/modules/ProductImages/ImageSuspensionNotice';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Select,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { BackButton, PageContainer } from '@/ui/components';
import { Icon, faRotateRight } from '@/ui/components/Icon';
import { useTranslate } from '@/translations';
import { useProductImageryQuery } from '@/api/ProductImages/hooks';
import { productImages } from '@/api/ProductImages/ProductImages';
import type {
  DailyImageBatch,
  ImageBatchItem,
} from '@/api/ProductImages/types';
import {
  ImageryAccess,
  ImageryError,
  ImageryStatus,
  imageryRoot,
  knownStatuses,
  positivePage,
} from './ProductImages';
import { ImageryContent } from './ProductImages.styled';
import { formatImageryTime, shortId } from './GenerationRecord';
import { PrivateImage } from './PrivateImage';
import { ImageQualityTag } from './ImageQualityTag';
import { VariantStatuses } from '@/modules/ProductImages/VariantImage';
import { VariantCounts } from '@/modules/ProductImages/VariantCounts';

const useListParameters = () => {
  const [params, setParams] = useSearchParams();
  const change = (values: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) =>
      value ? next.set(key, value) : next.delete(key),
    );
    setParams(next);
  };
  return { params, change };
};

export const ImageBatchList = () => {
  const { t } = useTranslate();
  const { params, change } = useListParameters();
  const query = {
    page: positivePage(params.get('batchPage')),
    pageSize: positivePage(params.get('batchSize') || '25'),
    status: params.get('batchStatus') || undefined,
    from: params.get('from') || undefined,
    to: params.get('to') || undefined,
  };
  const result = useProductImageryQuery(['batches', query], () =>
    productImages.batches(query),
  );
  if (result.isError)
    return (
      <ImageryError error={result.error} retry={() => void result.refetch()} />
    );
  return (
    <>
      <div className="imagery-toolbar">
        <label>
          {t('imagery.fromDate')}
          <DatePicker
            aria-label={t('imagery.fromDate')}
            value={query.from ? dayjs(query.from) : null}
            onChange={(value) =>
              change({
                from: value?.format('YYYY-MM-DD'),
                batchPage: undefined,
              })
            }
          />
        </label>
        <label>
          {t('imagery.toDate')}
          <DatePicker
            aria-label={t('imagery.toDate')}
            value={query.to ? dayjs(query.to) : null}
            onChange={(value) =>
              change({ to: value?.format('YYYY-MM-DD'), batchPage: undefined })
            }
          />
        </label>
        <Select
          aria-label={t('imagery.status')}
          placeholder={t('imagery.allStatuses')}
          allowClear
          value={query.status}
          onChange={(value) =>
            change({ batchStatus: value, batchPage: undefined })
          }
          options={['Running', 'Completed', 'Failed', 'Stopped'].map(
            (value) => ({ value, label: value }),
          )}
        />
        <Tooltip title={t('imagery.refresh')}>
          <Button
            aria-label={t('imagery.refresh')}
            icon={<Icon icon={faRotateRight} />}
            onClick={() => void result.refetch()}
            loading={result.isFetching}
          />
        </Tooltip>
      </div>
      <Table<DailyImageBatch>
        size="middle"
        rowKey="id"
        loading={result.isLoading}
        dataSource={result.data?.items ?? []}
        scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty description={t('imagery.noRecords')} /> }}
        pagination={{
          current: query.page,
          pageSize: query.pageSize,
          total: result.data?.totalCount ?? 0,
          showSizeChanger: true,
          onChange: (page, size) =>
            change({ batchPage: page.toString(), batchSize: size.toString() }),
        }}
        columns={[
          {
            title: t('imagery.batch'),
            width: 150,
            render: (_, batch) => (
              <Link
                className="imagery-record-id"
                to={`${imageryRoot}/batches/${batch.id}?${params}`}
              >
                {shortId(batch.id)}
              </Link>
            ),
          },
          {
            title: t('imagery.startedAt'),
            dataIndex: 'startedAt',
            width: 190,
            render: formatImageryTime,
          },
          {
            title: t('imagery.status'),
            dataIndex: 'status',
            width: 140,
            render: (status) => <ImageryStatus status={status} />,
          },
          {
            title: t('imagery.considered'),
            dataIndex: 'consideredCount',
            width: 110,
          },
          {
            title: t('imagery.started'),
            dataIndex: 'startedCount',
            width: 115,
          },
          {
            title: t('imagery.published'),
            dataIndex: 'publishedCount',
            width: 100,
          },
          { title: t('imagery.failed'), dataIndex: 'failedCount', width: 85 },
          {
            title: t('imagery.variantResults'),
            width: 260,
            render: (_, batch) => (
              <VariantCounts counts={batch.variantCounts} />
            ),
          },
          { title: t('imagery.skipped'), dataIndex: 'skippedCount', width: 85 },
          {
            title: t('imagery.deferred'),
            dataIndex: 'deferredCount',
            width: 95,
          },
        ]}
      />
    </>
  );
};

const ImageBatch = () => {
  const { t } = useTranslate();
  const { batchId = '' } = useParams();
  const { params, change } = useListParameters();
  const query = {
    page: positivePage(params.get('itemPage')),
    pageSize: positivePage(params.get('itemSize') || '25'),
    status: params.get('itemStatus') || undefined,
  };
  const result = useProductImageryQuery(
    ['batch', batchId, query],
    () => productImages.batch(batchId, query),
    (detail) => (detail.batch.status === 'Running' ? 10_000 : false),
  );
  if (result.isError)
    return (
      <ImageryError error={result.error} retry={() => void result.refetch()} />
    );
  if (!result.data) return <Spin />;
  const { batch, items, totalCount } = result.data;
  const backParams = new URLSearchParams(params);
  backParams.set('tab', 'batches');
  const back = `${imageryRoot}?${backParams}`;
  return (
    <>
      <BackButton title={t('imagery.batches')} linkTo={back} />
      <Breadcrumb
        className="mt-3"
        items={[
          { title: <Link to={back}>{t('imagery.batches')}</Link> },
          { title: shortId(batch.id) },
        ]}
      />
      <div className="imagery-heading">
        <h2>
          {t('imagery.batch')}{' '}
          <span className="imagery-record-id">{shortId(batch.id)}</span>
        </h2>
        <ImageryStatus status={batch.status} />
      </div>
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 1, md: 2 }}
        items={[
          {
            key: 'origin',
            label: t('imagery.origin'),
            children: batch.triggerOrigin,
          },
          {
            key: 'by',
            label: t('imagery.requestedBy'),
            children: batch.initiatedBy || '-',
          },
          {
            key: 'start',
            label: t('imagery.startedAt'),
            children: formatImageryTime(batch.startedAt),
          },
          {
            key: 'end',
            label: t('imagery.completedAt'),
            children: formatImageryTime(batch.completedAt),
          },
        ]}
      />
      <dl className="imagery-counts mt-4">
        {(
          [
            ['imagery.considered', batch.consideredCount],
            ['imagery.started', batch.startedCount],
            ['imagery.published', batch.publishedCount],
            ['imagery.rejected', batch.rejectedCount],
            ['imagery.failed', batch.failedCount],
            ['imagery.unknown', batch.unknownCount],
            ['imagery.stopped', batch.stoppedCount],
            ['imagery.skipped', batch.skippedCount],
            ['imagery.deferred', batch.deferredCount],
          ] as const
        ).map(([label, count]) => (
          <div key={label}>
            <dt>{t(label)}</dt>
            <dd>{count ?? '-'}</dd>
          </div>
        ))}
      </dl>
      <VariantCounts counts={batch.variantCounts} />
      <section className="imagery-section">
        <h3>{t('imagery.productResults')}</h3>
        <Table<ImageBatchItem>
          size="middle"
          rowKey="id"
          dataSource={items}
          scroll={{ x: 1050 }}
          onChange={(_, filters, __, extra) => {
            if (extra.action === 'filter') {
              change({
                itemStatus: filters.status?.[0]?.toString(),
                itemPage: undefined,
              });
            }
          }}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: totalCount,
            showSizeChanger: true,
            onChange: (page, size) =>
              change({ itemPage: page.toString(), itemSize: size.toString() }),
          }}
          columns={[
            {
              title: t('imagery.product'),
              width: 220,
              render: (_, item) => (
                <>
                  <Link
                    to={`${imageryRoot}/products/${item.productId}?${params}`}
                  >
                    {item.productCode}
                  </Link>
                  <div>{item.productDescription}</div>
                </>
              ),
            },
            {
              title: t('imagery.customer'),
              dataIndex: 'customerName',
              width: 150,
            },
            {
              title: t('imagery.result'),
              dataIndex: 'status',
              width: 125,
              filters: knownStatuses.map((value) => ({ text: value, value })),
              filterMultiple: false,
              filteredValue: query.status ? [query.status] : null,
              render: (status) => <ImageryStatus status={status} />,
            },
            {
              title: t('imagery.variantResults'),
              width: 130,
              render: (_, item) => <VariantStatuses image={item.outputImage} />,
            },
            {
              title: t('imagery.resultReason'),
              dataIndex: 'reason',
              width: 230,
              render: (value) => value || '-',
            },
            {
              title: t('imagery.output'),
              width: 145,
              render: (_, item) =>
                item.outputImage?.sourceKind === 'Generated' ? (
                  <div className="imagery-image-output">
                    <PrivateImage
                      src={
                        item.outputImage.thumbnailUrl ??
                        item.outputImage.contentUrl
                      }
                      assetId={item.outputImage.assetId}
                      thumbnail
                    />
                    <ImageQualityTag image={item.outputImage} />
                    <ImageSuspensionNotice image={item.outputImage} />
                  </div>
                ) : (
                  '-'
                ),
            },
            {
              title: t('imagery.run'),
              width: 160,
              render: (_, item) =>
                item.generationId ? (
                  <Link
                    className="imagery-record-id"
                    to={`${imageryRoot}/generations/${item.generationId}?${params}`}
                  >
                    RUN {shortId(item.generationId)}
                  </Link>
                ) : (
                  <span className="text-secondary">
                    {t('imagery.noPaidRun')}
                  </span>
                ),
            },
          ]}
        />
      </section>
    </>
  );
};

export const ImageBatchPage = () => (
  <ImageryAccess>
    <PageContainer>
      <ImageryContent>
        <ImageBatch />
      </ImageryContent>
    </PageContainer>
  </ImageryAccess>
);
